package com.tovtalk.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Bookmark
import androidx.compose.material.icons.filled.BookmarkBorder
import androidx.compose.material.icons.filled.ChatBubbleOutline
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Share
import androidx.compose.material.icons.automirrored.filled.Reply
import androidx.compose.material.icons.outlined.ChatBubbleOutline
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.automirrored.filled.VolumeOff
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

@Composable
fun VideoActionButton(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    activeIcon: androidx.compose.ui.graphics.vector.ImageVector = icon,
    count: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    isActive: Boolean = false,
    activeColor: Color = TovTalkTheme.colors.like,
    contentDescription: String? = null,
    showCount: Boolean = true,
    iconSize: Dp = TovTalkTheme.sizes.iconSm,
    buttonSize: Dp = TovTalkTheme.sizes.videoActionButton,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val activeScale by animateFloatAsState(
        targetValue = if (isActive) 1.15f else 1f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessLow
        ),
        label = "actionActiveScale"
    )
    val pressScale by animateFloatAsState(
        targetValue = if (isPressed) 0.88f else 1f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessMedium
        ),
        label = "actionPressScale"
    )
    val scale = activeScale * pressScale
    Column(
        modifier = modifier
            .sizeIn(minWidth = TovTalkTheme.sizes.minTouchTarget, minHeight = TovTalkTheme.sizes.minTouchTarget)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier
                // Keep a larger touch target via the Column's `sizeIn(minHeight/minWidth)`,
                // but visually pack the icon so the count sits closer to it.
                .size(iconSize)
                .scale(scale),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = if (isActive) activeIcon else icon,
                contentDescription = contentDescription,
                modifier = Modifier.size(iconSize),
                tint = if (isActive) activeColor else TovTalkTheme.colors.onBrand
            )
        }
        if (showCount) {
            // Minimal spacing between icon and its count text.
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.none))
            Text(
                text = count,
                style = TovTalkTheme.typography.stats,
                color = TovTalkTheme.colors.onBrand
            )
        }
    }
}

@Composable
fun VideoActionsColumn(
    isLiked: Boolean,
    likeCount: String,
    commentCount: String,
    shareCount: String,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit,
    onShareClick: () -> Unit,
    modifier: Modifier = Modifier,
    avatarUrl: String? = null,
    username: String? = null,
    onAvatarClick: () -> Unit = {},
    showFollowButton: Boolean = false,
    followButton: @Composable () -> Unit = {},
    isSaved: Boolean = false,
    saveCount: String = "0",
    onSaveClick: () -> Unit = {},
    showMuteButton: Boolean = false,
    isMuted: Boolean = true,
    onMuteClick: () -> Unit = {},
    onReportClick: () -> Unit = {},
    orderBottomToTop: Boolean = false,
    useLargeIcons: Boolean = false,
    commentsEnabled: Boolean = true,
    showReactionIcon: Boolean = true,
    hasStory: Boolean = false,
    onStoryClick: (() -> Unit)? = null,
) {
    val actionIconSize = if (useLargeIcons) TovTalkTheme.sizes.iconLg else TovTalkTheme.sizes.iconSm
    val actionButtonSize = if (useLargeIcons) TovTalkTheme.sizes.minTouchTarget else TovTalkTheme.sizes.videoActionButton
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        // Keep the profile row detached; spacing between action buttons is controlled in `actionsColumn`.
        verticalArrangement = Arrangement.Top
    ) {
        if (avatarUrl != null || username != null || showFollowButton) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
                modifier = Modifier.clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onAvatarClick
                )
            ) {
                if (avatarUrl != null) {
                    Box {
                        TovTalkAvatar(
                            imageUrl = avatarUrl,
                            // "Icon with the image itself" (avatar) should be larger.
                            size = TovTalkTheme.sizes.avatarMd,
                            name = username,
                            hasStory = hasStory && onStoryClick != null,
                            onStoryClick = onStoryClick
                        )
                        if (showFollowButton) {
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .offset(y = 6.dp)
                            ) {
                                followButton()
                            }
                        }
                    }
                }
                if (username != null) {
                    if (avatarUrl != null) {
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
                    }
                    Text(
                        text = "@$username",
                        style = TovTalkTheme.typography.username,
                        color = TovTalkTheme.colors.onBrand
                    )
                }
                if (showFollowButton && avatarUrl == null) {
                    if (username != null) {
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
                    }
                    followButton()
                }
            }
            // Push the action icons slightly down so the avatar appears "higher".
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
        }
        Column(
            // Add small spacing between action buttons.
            verticalArrangement = when {
                orderBottomToTop -> Arrangement.spacedBy(TovTalkTheme.spacing.xxs, Alignment.Bottom)
                else -> Arrangement.spacedBy(TovTalkTheme.spacing.xxs)
            },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            if (orderBottomToTop) {
                // Declared top-to-bottom: Like, Chat, Save, Share, Mute (at the very bottom)
                VideoActionButton(
                    icon = Icons.Filled.Favorite,
                    activeIcon = Icons.Filled.Favorite,
                    count = likeCount,
                    onClick = onLikeClick,
                    isActive = isLiked,
                    activeColor = TovTalkTheme.colors.like,
                    contentDescription = StringProvider.get("accessibility_like"),
                    iconSize = actionIconSize,
                    buttonSize = actionButtonSize
                )
                if (commentsEnabled && showReactionIcon) {
                    VideoActionButton(
                        icon = Icons.Outlined.ChatBubbleOutline,
                        activeIcon = Icons.Outlined.ChatBubbleOutline,
                        count = commentCount,
                        onClick = onCommentClick,
                        contentDescription = StringProvider.get("accessibility_comment"),
                        iconSize = actionIconSize,
                        buttonSize = actionButtonSize
                    )
                }
                VideoActionButton(
                    icon = Icons.Filled.BookmarkBorder,
                    activeIcon = Icons.Filled.Bookmark,
                    count = saveCount,
                    onClick = onSaveClick,
                    isActive = isSaved,
                    activeColor = TovTalkTheme.colors.brand,
                    contentDescription = StringProvider.get("accessibility_save"),
                    showCount = true,
                    iconSize = actionIconSize,
                    buttonSize = actionButtonSize
                )
                VideoActionButton(
                    icon = Icons.AutoMirrored.Filled.Reply,
                    count = shareCount,
                    onClick = onShareClick,
                    contentDescription = StringProvider.get("accessibility_share"),
                    showCount = false,
                    iconSize = actionIconSize,
                    buttonSize = actionButtonSize
                )
                VideoActionButton(
                    icon = Icons.Outlined.Flag,
                    count = "",
                    onClick = onReportClick,
                    contentDescription = StringProvider.get("report_reel_action"),
                    showCount = false,
                    iconSize = actionIconSize,
                    buttonSize = actionButtonSize
                )
                if (showMuteButton) {
                    VideoActionButton(
                        icon = Icons.AutoMirrored.Filled.VolumeOff,
                        activeIcon = Icons.AutoMirrored.Filled.VolumeUp,
                        count = "",
                        onClick = onMuteClick,
                        isActive = !isMuted,
                        activeColor = TovTalkTheme.colors.brand,
                        contentDescription = if (isMuted) StringProvider.get("accessibility_unmute") else StringProvider.get("accessibility_mute"),
                        showCount = false,
                        iconSize = actionIconSize,
                        buttonSize = actionButtonSize
                    )
                }
            } else {
                VideoActionButton(
                    icon = Icons.Filled.Favorite,
                    activeIcon = Icons.Filled.Favorite,
                    count = likeCount,
                    onClick = onLikeClick,
                    isActive = isLiked,
                    activeColor = TovTalkTheme.colors.like,
                    contentDescription = StringProvider.get("accessibility_like")
                )
                if (commentsEnabled && showReactionIcon) {
                    VideoActionButton(
                        icon = Icons.Outlined.ChatBubbleOutline,
                        activeIcon = Icons.Outlined.ChatBubbleOutline,
                        count = commentCount,
                        onClick = onCommentClick,
                        contentDescription = StringProvider.get("accessibility_comment")
                    )
                }
                VideoActionButton(
                    icon = Icons.Filled.BookmarkBorder,
                    activeIcon = Icons.Filled.Bookmark,
                    count = saveCount,
                    onClick = onSaveClick,
                    isActive = isSaved,
                    activeColor = TovTalkTheme.colors.brand,
                    contentDescription = StringProvider.get("accessibility_save"),
                    showCount = false
                )
                VideoActionButton(
                    icon = Icons.AutoMirrored.Filled.Reply,
                    count = shareCount,
                    onClick = onShareClick,
                    contentDescription = StringProvider.get("accessibility_share"),
                    showCount = false
                )
                VideoActionButton(
                    icon = Icons.Outlined.Flag,
                    count = "",
                    onClick = onReportClick,
                    contentDescription = StringProvider.get("report_reel_action"),
                    showCount = false
                )
                if (showMuteButton) {
                    VideoActionButton(
                        icon = Icons.AutoMirrored.Filled.VolumeOff,
                        activeIcon = Icons.AutoMirrored.Filled.VolumeUp,
                        count = "",
                        onClick = onMuteClick,
                        isActive = !isMuted,
                        activeColor = TovTalkTheme.colors.brand,
                        contentDescription = if (isMuted) StringProvider.get("accessibility_unmute") else StringProvider.get("accessibility_mute"),
                        showCount = false
                    )
                }
            }
        }
    }
}

/**
 * Follow button for reel overlay – shows localized "Follow" text (same as profile_follow).
 */
@Composable
fun ReelFollowButton(
    onClick: () -> Unit,
    size: Dp = TovTalkTheme.sizes.avatarSm
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(
        targetValue = if (isPressed) 0.92f else 1f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessMedium
        ),
        label = "reelFollowButtonScale"
    )
    Box(
        modifier = Modifier
            .scale(scale)
            .height(size)
            .clip(TovTalkTheme.shapes.full)
            .background(TovTalkTheme.colors.brand)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            )
            .padding(horizontal = TovTalkTheme.spacing.sm),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = StringProvider.get("profile_follow"),
            style = TovTalkTheme.typography.labelSmall,
            color = TovTalkTheme.colors.onBrand
        )
    }
}

@Composable
fun ReelFollowPlusButton(
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .size(TovTalkTheme.sizes.iconSm)
            .clip(CircleShape)
            .background(TovTalkTheme.colors.error)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = "+",
            style = TovTalkTheme.typography.labelSmall,
            color = TovTalkTheme.colors.onBrand
        )
    }
}

@Composable
fun DoubleTapLikeAnimation(
    visible: Boolean,
    modifier: Modifier = Modifier,
) {
    AnimatedVisibility(
        visible = visible,
        modifier = modifier,
        enter = scaleIn(
            initialScale = 0.5f,
            animationSpec = spring(
                dampingRatio = Spring.DampingRatioMediumBouncy,
                stiffness = Spring.StiffnessLow
            )
        ) + fadeIn(),
        exit = scaleOut(targetScale = 1.2f) + fadeOut()
    ) {
        Icon(
            imageVector = Icons.Filled.Favorite,
            contentDescription = null,
            modifier = Modifier.size(TovTalkTheme.sizes.thumbnailMd),
            tint = TovTalkTheme.colors.like
        )
    }
}
